import { ed25519 } from "@noble/curves/ed25519.js";
import { blake3 } from "@noble/hashes/blake3.js";

import { bytesToHex, concatBytes, u64LE, vu64 } from "./bytes";

/**
 * `SeedContribEnvelope` wire-format encoder + canonical signing
 * payload computation. Byte-equal to
 * `arkhe-holdem-actions::envelope::SeedContribEnvelope` (Rust
 * postcard `enc_seed_contrib_envelope` / `sign_payload_seed_contrib`).
 *
 * Postcard wire layout:
 *
 *     table_id: [u8; 16]             // 16 fixed bytes
 *     hand_seq: u64                  // postcard varint
 *     player_id: [u8; 32]            // 32 fixed bytes
 *     contribution: [u8; 32]         // 32 fixed bytes
 *     sig: [u8; 64]                  // 64 fixed bytes
 */

/** Domain separator bound into the BLAKE3 signing payload prefix
 *  (`sign_payload_seed_contrib`). Distinct from `DOMAIN_ACTION` so a
 *  signature minted for one envelope flavor never verifies against
 *  the other. */
export const DOMAIN_SEED_CONTRIB = new TextEncoder().encode(
  "arkhe-holdem::seed-contrib::v1",
);

/**
 * Canonical 32-byte signing payload for a seed contribution envelope.
 * Layout matches `arkhe_holdem_actions::envelope::sign_payload_seed_contrib`
 * (wire minor 20):
 *
 *     BLAKE3(DOMAIN_SEED_CONTRIB || table_id || hand_seq_le ||
 *            player_id (32 B) || contribution (32 B) || commit (32 B))
 *
 * The trailing `commit` binds the signature to the specific
 * `SeedRoundSnapshot` the server sealed via `server_commit`. A
 * round identity shift (stale-seed sweep abort + fresh round on the
 * same `hand_seq`) re-seals the commit, so an envelope signed
 * against the prior commit can never replay against the fresh
 * round — the BE re-derives the same digest from its own snapshot,
 * verifies against the public key, and rejects on mismatch.
 *
 * The commit itself is NOT carried inside the envelope wire bytes
 * (no shape change on `encSeedContribEnvelope`); the BE
 * reconstructs it from its own snapshot during verification.
 */
export function signPayloadSeedContrib(
  tableId: Uint8Array,
  handSeq: bigint,
  playerId: Uint8Array,
  contribution: Uint8Array,
  commit: Uint8Array,
): Uint8Array {
  if (tableId.length !== 16) throw new RangeError("seed-contrib: tableId 16B");
  if (playerId.length !== 32)
    throw new RangeError("seed-contrib: playerId 32B");
  if (contribution.length !== 32) {
    throw new RangeError("seed-contrib: contribution 32B");
  }
  if (commit.length !== 32) {
    throw new RangeError("seed-contrib: commit 32B");
  }
  const payload = concatBytes(
    DOMAIN_SEED_CONTRIB,
    tableId,
    u64LE(handSeq),
    playerId,
    contribution,
    commit,
  );
  return blake3(payload, { dkLen: 32 });
}

export interface SeedContribEnvelopeFields {
  tableId: Uint8Array; // 16 bytes
  handSeq: bigint;
  playerId: Uint8Array; // 32 bytes
  contribution: Uint8Array; // 32 bytes
  sig: Uint8Array; // 64 bytes
}

/**
 * Postcard-encode a `SeedContribEnvelope` for transport. Layout
 * mirrors `arkhe-holdem-actions::envelope::SeedContribEnvelope`
 * (Rust postcard `enc_seed_contrib_envelope`).
 */
export function encSeedContribEnvelope(
  env: SeedContribEnvelopeFields,
): Uint8Array {
  if (env.tableId.length !== 16)
    throw new RangeError("encSeedContribEnvelope: tableId 16B");
  if (env.playerId.length !== 32)
    throw new RangeError("encSeedContribEnvelope: playerId 32B");
  if (env.contribution.length !== 32) {
    throw new RangeError("encSeedContribEnvelope: contribution 32B");
  }
  if (env.sig.length !== 64)
    throw new RangeError("encSeedContribEnvelope: sig 64B");
  return concatBytes(
    env.tableId,
    vu64(env.handSeq),
    env.playerId,
    env.contribution,
    env.sig,
  );
}

/**
 * Sign + encode a SeedContribEnvelope in one shot. The caller
 * supplies the 32-byte ed25519 signing seed; the public key is
 * derived from the same seed so the envelope's `player_id` always
 * matches the seated roster's record.
 *
 * Contribution material is supplied by the caller — the seed-round
 * orchestrator pulls 32 fresh bytes from `crypto.getRandomValues`
 * (browser-native CSPRNG, ADR-008 Q3) right before calling this.
 * Mixing in deterministic material here would let the server
 * precompute the final aggregate during the commit phase, violating
 * ADR-001 G3.
 *
 * The 32-byte `commit` argument (wire minor 20) is the server-sealed
 * BLAKE3 commit carried on `SeedRoundView.commit`; binding it into
 * the signing payload pins the signature to the round identity, so
 * a stale-seed sweep abort + fresh-round-on-the-same-hand_seq cycle
 * can no longer accept a replayed envelope (the fresh round seals a
 * new commit + the BE verification recomputes against it).
 */
export function signAndEncodeSeedContrib(args: {
  tableId: Uint8Array;
  handSeq: bigint;
  signingKey: Uint8Array; // 32-byte seed
  contribution: Uint8Array; // 32 fresh CSPRNG bytes
  commit: Uint8Array; // 32-byte server-sealed BLAKE3 commit
}): {
  envelope: Uint8Array;
  envelopeHex: string;
  playerId: Uint8Array;
  sig: Uint8Array;
} {
  if (args.signingKey.length !== 32) {
    throw new RangeError("signAndEncodeSeedContrib: signingKey 32B");
  }
  if (args.contribution.length !== 32) {
    throw new RangeError("signAndEncodeSeedContrib: contribution 32B");
  }
  if (args.commit.length !== 32) {
    throw new RangeError("signAndEncodeSeedContrib: commit 32B");
  }
  const playerId = ed25519.getPublicKey(args.signingKey);
  const payload = signPayloadSeedContrib(
    args.tableId,
    args.handSeq,
    playerId,
    args.contribution,
    args.commit,
  );
  const sig = ed25519.sign(payload, args.signingKey);
  const envelope = encSeedContribEnvelope({
    tableId: args.tableId,
    handSeq: args.handSeq,
    playerId,
    contribution: args.contribution,
    sig,
  });
  return { envelope, envelopeHex: bytesToHex(envelope), playerId, sig };
}
